/**
 * PhotoViewer — listing photo gallery for the PDP, ported from od-prototypes photo-viewer v2.
 *
 * Layout
 * - Fills its container's width and restores the desktop gallery's 8:3 frame. The hero
 *   (photo 0) stays a true 3:2 while the four supporting tiles share the remaining width.
 * - Later columns are sized from the stage height: wide photos get a full-height column
 *   (aspect 1.5), others stack two square tiles per column, with a 4px gap.
 *
 * Behaviour
 * - The stage never scrolls natively. It moves via a horizontal swipe over the photos or
 *   ← / →, always settling on a column boundary. Phones show every listing photo with the
 *   iOS-style page indicator and support long-press horizontal scrubbing.
 * - Counter (top-left) and a share / heart pill (top-right) sit 12px inside the photo area.
 * - The lightbox fades in at full size, with vertical photo scrolling on desktop and
 *   horizontal swiping on phones. The zoom layer is portalled to <body>.
 * - prefers-reduced-motion: every animation becomes a cut.
 */
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

import { Button } from '@opendoor/atoms/button';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconLibraryPhoto, IconX } from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  buildWorkerPhotoUrl,
  getLightboxThumbnailUrl,
  pickFirstPresizedUrl,
} from 'components/marketplace/helpers/presizedPhotos';
import { GalleryMediaActions } from 'components/marketplace/property/media/GalleryMediaActions';
import { PropertyPhotoPlaceholder } from 'components/marketplace/shared/PropertyPhoto';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';
import { animate } from 'framer-motion';
import { toImagesWorkerSourceUrl } from 'helpers/imageUrl';
import { createPortal } from 'react-dom';

import type { PropertyDetailsType } from '../types';

export interface ViewerPhoto {
  /** xl URL for the lightbox. The grid uses `srcSet` of the same 3:2 crop. */
  src: string;
  /** Responsive candidates for the grid and the server-rendered hero. */
  srcSet?: string;
  /** Small render for thumbnail strips. */
  thumb: string;
  alt: string;
  /** true → full-height column; false → stacked two per column. */
  wide: boolean;
}

/* ---- locked design values ---------------------------------------------------- */
const CONFIG = {
  grid: {
    padding: 0,
    gap: 4,
    frameRatio: 8 / 3,
    heroRatio: 3 / 2,
    topRadius: 14,
  },
  columnsAfter: {
    stackedAspect: 1,
    wideAspect: 1.5,
  },
  zoom: {
    open: { duration: 0.12, ease: [0.22, 0.61, 0.36, 1] as [number, number, number, number] },
    close: { duration: 0.12, ease: [0.22, 0.61, 0.36, 1] as [number, number, number, number] },
    navigate: { duration: 0.12, ease: [0.2, 0.8, 0.2, 1] as [number, number, number, number] },
    sideInset: 96,
    stackGap: 4,
    snap: true,
    backdrop: 'color-mix(in srgb, var(--color-od-bg-scrim) 90%, transparent)',
  },
};
type ViewerConfig = typeof CONFIG;
const INK = 'var(--color-od-content-primary)';
const CANVAS = 'var(--color-od-bg-secondary)';
/** Height of the site header the zoom bar replaces while a photo is open. */
const BAR_H = 72;
const ZOOM_PHOTO_BOTTOM = 24;
const ZOOM_PHOTO_TOP = 24;
const MOBILE_ZOOM_DETAILS_GAP = 16;
/** Zoomed photos come from the 3:2 image preset. */
const ZOOM_PHOTO_RATIO = 3 / 2;

/** Below this width the viewer behaves like a phone: one photo at a time. */
const PHONE_MAX_WIDTH = 768;
const isPhone = (W: number) => W < PHONE_MAX_WIDTH;
const MOBILE_SCRUB_HOLD_MS = 500;
const MOBILE_SCRUB_DRAG_MULTIPLIER = 1.5;

export const mobilePhotoScrubIndex = ({
  count,
  currentX,
  startIndex,
  startX,
  stripWidth,
}: {
  count: number;
  currentX: number;
  startIndex: number;
  startX: number;
  stripWidth: number;
}) => {
  if (count <= 0 || stripWidth <= 0) return 0;
  const slotWidth = stripWidth / count;
  const indexDelta = Math.round(((currentX - startX) * MOBILE_SCRUB_DRAG_MULTIPLIER) / slotWidth);
  return Math.max(0, Math.min(count - 1, startIndex + indexDelta));
};

function geometry(W: number, g: ViewerConfig['grid']) {
  if (isPhone(W)) {
    const heroW = W - 2 * g.padding;
    const heroH = heroW / g.heroRatio;
    return { heroW, heroH, frameH: heroH + 2 * g.padding };
  }
  const heroH = (W - 2 * g.padding) / g.frameRatio;
  const heroW = heroH * g.heroRatio;
  return { heroW, heroH, frameH: heroH + 2 * g.padding };
}

const CSS = `
  .pv-root{
    --bg: var(--color-od-bg-primary);
    --gap: 4px;
    --pill-h: 46px;
    --hero-w: 56.25%;
    --hero-h: 100%;
    --od-canvas: ${CANVAS};
    position:relative; width:100%;
    padding:var(--pad, 16px);
    background:var(--bg);
    overflow:hidden;
    -webkit-font-smoothing:antialiased;
    /* Before the engine measures, hold the original desktop gallery frame. */
    aspect-ratio: 8 / 3;
  }
  /* The server-rendered hero: exactly where the hero cell goes, so the grid replaces it seamlessly. */
  .pv-root .ssr-hero{
    position:absolute; left:0; top:0; height:100%; width:56.25%;
    object-fit:cover; display:block; border-radius:var(--stage-radius, 14px) 0 0 var(--stage-radius, 14px);
  }
  .pv-root .ssr-hero[hidden]{ display:none; }
  .pv-root.empty .empty-hero{ position:absolute; inset:0; }
  .pv-root.empty .empty-hero > *{ height:100%; width:100%; }
  .pv-root.empty .scrubber,
  .pv-root.empty .mobile-page-indicator,
  .pv-root.empty .counter,
  .pv-root.empty .actions{ display:none; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-root{ aspect-ratio: 3 / 2; --hero-w: 100%; }
    .pv-root .ssr-hero{ width:100%; border-radius:var(--stage-radius, 14px); }
  }
  .pv-root, .pv-root *{ box-sizing:border-box; }
  .pv-root .viewer{ position:absolute; inset:var(--pad, 16px); display:flex; flex-direction:column; }
  .pv-root .stage{
    flex:1 1 auto; position:relative; overflow:hidden; touch-action:pan-y;
    border-radius:var(--stage-radius, 14px);
  }
  .pv-root .track{ display:flex; gap:var(--gap); height:100%; align-items:stretch; width:max-content; }
  .pv-root .col{ flex:0 0 auto; height:100%; display:flex; flex-direction:column; gap:var(--gap); }
  .pv-root .cell{ position:relative; flex:1 1 0; overflow:hidden; border-radius:4px; background:var(--color-od-bg-tertiary); cursor:pointer; }
  .pv-root .col:first-child .cell{ border-radius:14px 4px 4px 14px; }
  .pv-root .col:nth-child(3) .cell:first-child{ border-top-right-radius:14px; }
  .pv-root .col:nth-child(3) .cell:last-child{ border-bottom-right-radius:14px; }
  .pv-root .col:only-child .cell{ border-radius:14px; }
  .pv-root .cell img{ width:100%; height:100%; object-fit:cover; display:block; user-select:none; -webkit-user-drag:none; }
  .pv-root .stage img{ -webkit-touch-callout:none; }
  .pv-root .hero-overlay{
    position:absolute; left:0; top:0; width:var(--hero-w); height:var(--hero-h);
    z-index:25; pointer-events:none;
  }
  .pv-root .hero-overlay > *{ pointer-events:none; }
  .pv-root .tour-3d{
    position:absolute; left:0; top:0; width:var(--hero-w); height:var(--hero-h);
    z-index:26; pointer-events:none;
  }
  .pv-root .gallery-media-actions{
    position:absolute; right:10px; top:10px; z-index:26;
    display:flex; align-items:center; gap:0; pointer-events:auto;
    border-radius:999px;
  }
  .pv-root .gallery-media-actions[data-grouped='true']{
    padding:1px;
    border:1px solid color-mix(in srgb, var(--color-od-content-primary) 15%, transparent);
    background:color-mix(in srgb, var(--color-od-bg-primary) 72%, transparent);
    box-shadow:0 3px 12px color-mix(in srgb, var(--color-od-content-primary) 9%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 42%, transparent);
    -webkit-backdrop-filter:blur(18px) saturate(160%);
    backdrop-filter:blur(18px) saturate(160%);
  }
  .pv-root .gallery-action-button,
  .pv-zoom .zoom-close{
    height:36px; padding:0 12px;
    display:flex; align-items:center; justify-content:center; gap:8px;
    border-radius:999px;
    border:1px solid color-mix(in srgb, var(--color-od-content-primary) 15%, transparent) !important;
    background:color-mix(in srgb, var(--color-od-bg-primary) 72%, transparent) !important;
    box-shadow:0 4px 14px color-mix(in srgb, var(--color-od-content-primary) 12%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 55%, transparent) !important;
    -webkit-backdrop-filter:blur(18px) saturate(160%);
    backdrop-filter:blur(18px) saturate(160%);
    font-family:inherit; font-size:var(--text-label-sm);
    font-weight:var(--text-label-sm--font-weight); line-height:var(--text-label-sm--line-height);
    letter-spacing:var(--text-label-sm--letter-spacing);
    transition:background-color .16s ease;
  }
  .pv-root .gallery-media-action,
  .pv-zoom .zoom-close{
    background:color-mix(in srgb, var(--color-od-bg-primary) 48%, transparent) !important;
    box-shadow:0 3px 12px color-mix(in srgb, var(--color-od-content-primary) 9%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 42%, transparent) !important;
    cursor:pointer;
    pointer-events:auto;
  }
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action{
    width:36px; padding:0;
    border:0 !important; background:transparent !important; box-shadow:none !important;
    -webkit-backdrop-filter:none; backdrop-filter:none;
  }
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-label{ display:none; }
  .pv-root .show-all-photos{
    position:absolute; right:10px; bottom:10px; z-index:26;
    background:color-mix(in srgb, var(--color-od-bg-primary) 88%, transparent) !important;
    box-shadow:0 4px 16px color-mix(in srgb, var(--color-od-content-primary) 16%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-od-bg-primary) 78%, transparent) !important;
    cursor:pointer;
  }
  .pv-zoom .zoom-close:hover,
  .pv-zoom .zoom-close:focus-visible,
  .pv-root .gallery-media-action:hover,
  .pv-root .gallery-media-action:focus-visible,
  .pv-root .show-all-photos:hover,
  .pv-root .show-all-photos:focus-visible,
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action:hover,
  .pv-root .gallery-media-actions[data-grouped='true'] .gallery-media-action:focus-visible{
    background:color-mix(in srgb, var(--color-od-bg-primary) 96%, transparent) !important;
  }

  .pv-zoom .zoom-close{ position:relative; width:32px; height:32px; padding:0; }
  .pv-zoom .zoom-close::before{ content:''; position:absolute; inset:-6px; }
  .pv-zoom{ position:fixed; inset:0; z-index:3000; display:none; cursor:default; --od-canvas: ${CANVAS}; --zoom-rail-gap:32px; -webkit-font-smoothing:antialiased; }
  .pv-zoom:focus,
  .pv-restored-focus:focus-visible{ outline:none !important; }
  .pv-zoom.on{ display:block; }
  .pv-zoom *{ box-sizing:border-box; }
  .pv-zoom .zoom-backdrop{
    position:absolute; inset:0; opacity:0;
    -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px);
  }
  .pv-zoom .zoom-scroll{
    position:absolute; left:0; top:0; width:100%; overflow-y:auto; overflow-x:hidden;
    overscroll-behavior:contain;
    display:flex; flex-direction:column; gap:var(--zoom-gap, 4px);
    opacity:0; pointer-events:none;
  }
  .pv-zoom .zoom-scroll.snap{ scroll-snap-type:y mandatory; }
  .pv-zoom .zoom-scroll.live{ opacity:1; pointer-events:auto; }
  .pv-zoom .zoom-slot{
    flex:none; width:100%; height:var(--slot-h, 100%); display:flex; align-items:center; justify-content:center;
    padding-left:var(--zoom-left-inset); padding-right:var(--zoom-right-inset);
    scroll-snap-align:start; overflow:hidden; cursor:default;
  }
  .pv-zoom .zoom-frame{
    position:relative; width:var(--photo-w, 100%); height:var(--photo-h, 100%); overflow:hidden; cursor:zoom-in;
  }
  .pv-zoom .zoom-frame img{
    width:100%; height:100%; object-fit:contain; display:block; -webkit-user-drag:none;
    transform-origin:0 0; transition:transform .08s linear;
  }
  .pv-zoom .zoom-frame.toggling img{ transition:transform .25s ease; }
  .pv-zoom .zoom-frame.magnified{ cursor:zoom-out; touch-action:none; }
  .pv-zoom .zoom-frame.panning img{ transition:none; }
  .pv-zoom .zoom-scrubber{
    position:absolute; top:var(--zoom-y); right:var(--zoom-rail-gap); z-index:45;
    --zoom-thumb-width:52px; --zoom-thumb-height:38px;
    width:64px; height:var(--photo-h); padding:6px;
    display:flex; flex-direction:column; align-items:flex-end; gap:4px; overflow-y:auto; overflow-x:hidden;
    opacity:0; scrollbar-width:none; overscroll-behavior-y:contain;
  }
  .pv-zoom .zoom-scrubber::-webkit-scrollbar{ display:none; }
  .pv-zoom .zoom-thumb{
    position:relative; flex:0 0 calc(var(--zoom-thumb-height) * .65); width:var(--zoom-thumb-width); height:calc(var(--zoom-thumb-height) * .65);
    padding:0; border:0; border-radius:6px; background:transparent; cursor:pointer;
    transition:flex-basis .2s cubic-bezier(.2,.8,.2,1), height .2s cubic-bezier(.2,.8,.2,1);
  }
  .pv-zoom .zoom-thumb::before{ content:''; position:absolute; inset:-2px -4px; }
  .pv-zoom .zoom-thumb-frame{
    position:absolute; right:0; top:50%; display:block; width:var(--zoom-thumb-width); height:var(--zoom-thumb-height); border-radius:6px;
    outline:2px solid transparent; outline-offset:-2px; overflow:hidden;
    opacity:calc(.75 * var(--thumb-rail-opacity, 1)); transform:translateY(-50%) scale(.65); transform-origin:right center;
    transition:transform .2s cubic-bezier(.2,.8,.2,1), opacity .2s ease, outline-color .2s ease;
  }
  .pv-zoom .zoom-thumb img{
    width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none;
  }
  .pv-zoom .zoom-thumb.outer{ flex-basis:calc(var(--zoom-thumb-height) * .75); height:calc(var(--zoom-thumb-height) * .75); }
  .pv-zoom .zoom-thumb.outer .zoom-thumb-frame{ opacity:calc(.85 * var(--thumb-rail-opacity, 1)); transform:translateY(-50%) scale(.75); }
  .pv-zoom .zoom-thumb.near{ flex-basis:calc(var(--zoom-thumb-height) * .9); height:calc(var(--zoom-thumb-height) * .9); }
  .pv-zoom .zoom-thumb.near .zoom-thumb-frame{ opacity:calc(.95 * var(--thumb-rail-opacity, 1)); transform:translateY(-50%) scale(.9); }
  .pv-zoom .zoom-thumb.previewing,
  .pv-zoom .zoom-thumb.active{ flex-basis:var(--zoom-thumb-height); height:var(--zoom-thumb-height); }
  .pv-zoom .zoom-thumb.previewing .zoom-thumb-frame,
  .pv-zoom .zoom-thumb.active .zoom-thumb-frame{ opacity:1; transform:translateY(-50%) scale(1); }
  .pv-zoom .zoom-thumb.active .zoom-thumb-frame{ outline-color:var(--color-od-bg-primary); }
  .pv-zoom .zoom-thumb:focus-visible{ outline:2px solid var(--color-od-border-inverse-primary); outline-offset:2px; }
  .pv-zoom .zoom-thumb-preview{
    position:absolute; right:calc(var(--zoom-rail-gap) + 76px); top:${BAR_H}px; z-index:46; width:204px; padding:5px;
    border-radius:12px;
    box-shadow:0 8px 32px color-mix(in srgb, var(--color-od-bg-scrim) 24%, transparent);
    opacity:0; visibility:hidden; pointer-events:none; transform:translateX(8px) scale(.96); transform-origin:right center;
    transition:opacity .15s ease, transform .2s cubic-bezier(.2,.8,.2,1), top .2s cubic-bezier(.2,.8,.2,1), visibility .15s;
  }
  .pv-zoom .zoom-thumb-preview.on{ opacity:1; visibility:visible; transform:translateX(0) scale(1); }
  .pv-zoom .zoom-thumb-preview img{ display:block; width:100%; aspect-ratio:3 / 2; object-fit:cover; border-radius:8px; }
  .pv-zoom .zoom-thumb-preview-caption{ display:flex; align-items:center; justify-content:flex-end; padding:7px 5px 3px; }
  .pv-zoom .zoom-thumb-preview-count{ font-size:var(--text-body-xs); line-height:var(--text-body-xs--line-height); color:color-mix(in srgb, var(--color-od-content-secondary) 75%, var(--color-od-content-primary)); font-variant-numeric:tabular-nums; }
  .pv-root .counter, .pv-zoom .counter{
    position:absolute; top:12px; left:12px; z-index:30;
    height:40px; display:flex; align-items:center;
    background:var(--od-canvas); color:${INK}; font-size:var(--text-label-xs); letter-spacing:var(--text-label-xs--letter-spacing);
    font-weight:var(--text-label-xs--font-weight); line-height:var(--text-label-xs--line-height);
    padding:0 11px; border-radius:999px; font-variant-numeric:tabular-nums;
  }
  .pv-root .counter{ display:none; }

  .pv-root .scrubber{
    position:absolute; left:0; right:0; bottom:26px; z-index:20;
    display:none; align-items:center; justify-content:center; padding:0 24px; pointer-events:none;
  }
  .pv-root .strip-wrap{ position:relative; pointer-events:auto; max-width:100%; }
  .pv-root .pill{
    position:relative; height:var(--pill-h); border-radius:10px;
    background:color-mix(in srgb, var(--color-od-bg-inverse-primary) 72%, transparent); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
    cursor:grab; touch-action:none;
  }
  .pv-root .pill.scrubbing{ cursor:grabbing; }
  .pv-root .pill::after{ content:''; position:absolute; left:-8px; right:-8px; top:-12px; bottom:-12px; }
  .pv-root .window{
    position:absolute; background:var(--color-od-bg-primary); border-radius:10px;
    box-shadow:0 2px 10px color-mix(in srgb, var(--color-od-bg-scrim) 22%, transparent); z-index:1; pointer-events:none;
  }
  .pv-root .clip{
    position:absolute; inset:0; border-radius:inherit; overflow:hidden;
    -webkit-mask-image:linear-gradient(to right, transparent 0, var(--color-od-bg-scrim) var(--fadeL, 0px), var(--color-od-bg-scrim) calc(100% - var(--fadeR, 0px)), transparent 100%);
    mask-image:linear-gradient(to right, transparent 0, var(--color-od-bg-scrim) var(--fadeL, 0px), var(--color-od-bg-scrim) calc(100% - var(--fadeR, 0px)), transparent 100%);
  }
  .pv-root .rail{ position:absolute; top:0; left:0; height:100%; will-change:transform; }
  .pv-root .thumbs{ position:absolute; inset:0; z-index:2; }
  .pv-root .thumb{ position:absolute; top:50%; left:0; border-radius:4px; overflow:hidden; background:var(--color-od-bg-inverse-secondary); transform-origin:left center; }
  .pv-root .thumb img{ width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none; }
  .pv-root .thumb:hover{ filter:brightness(1.18); }
  .pv-root .mobile-page-indicator{ display:none; }
  .pv-zoom .counter{
    top:12px; left:auto; right:12px; pointer-events:none;
    height:24px; padding:0 8px; font-size:var(--text-label-xs); line-height:var(--text-label-xs--line-height);
  }
  .pv-zoom .counter, .pv-zoom .zoom-thumb-preview{
    background:color-mix(in srgb, var(--color-od-bg-primary) 65%, transparent); color:var(--color-od-content-primary);
    -webkit-backdrop-filter:blur(12px); backdrop-filter:blur(12px);
  }

  .pv-zoom .counter[hidden], .pv-zoom .zoom-details[hidden]{ display:none; }

  /* The bar that stands in for the site header while zoomed: same 72px height, new content. */
  .pv-zoom .zoom-bar{
    position:absolute; top:0; left:0; right:0; height:${BAR_H}px; z-index:40; opacity:0;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 24px; color:var(--color-od-content-inverse-primary); pointer-events:none;
  }
  .pv-zoom .zoom-bar > *{ pointer-events:auto; }
  .pv-zoom .zoom-details{
    position:absolute; bottom:0; left:0; right:0; z-index:40; pointer-events:none;
    display:flex; flex-direction:column; align-items:flex-start; justify-content:flex-end; gap:2px; padding:48px 24px 20px;
    background:linear-gradient(to bottom, transparent, color-mix(in srgb, var(--color-od-bg-scrim) 72%, transparent));
    color:var(--color-od-content-inverse-primary); text-align:left;
    text-shadow:0 1px 4px color-mix(in srgb, var(--color-od-bg-scrim) 45%, transparent);
  }
  .pv-zoom .zoom-mobile-details{ display:none; }
  .pv-zoom .zoom-details-address{
    margin:0; font-size:var(--text-label-sm); line-height:var(--text-label-sm--line-height); font-weight:var(--text-label-sm--font-weight);
  }
  .pv-zoom .zoom-details-facts{
    margin:0; font-size:var(--text-body-sm); line-height:var(--text-body-sm--line-height); color:var(--color-od-content-inverse-secondary);
  }
  .pv-root .stage.swiping .cell{ cursor:grabbing; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-root .ssr-hero,
    .pv-root .stage,
    .pv-root .cell,
    .pv-root .col:first-child .cell,
    .pv-root .col:nth-child(3) .cell:first-child,
    .pv-root .col:nth-child(3) .cell:last-child,
    .pv-root .col:only-child .cell{ border-radius:0; }
    .pv-root .show-all-photos{ display:none; }
    .pv-root .gallery-media-action{ width:36px; padding:0; }
    .pv-root .gallery-media-label{ display:none; }
    .pv-root .stage.mobile-scrubbing{ touch-action:none; }
    .pv-root .mobile-page-indicator{
      position:absolute; left:50px; right:50px; bottom:12px; z-index:22;
      height:8px; display:flex; align-items:center; gap:var(--indicator-gap, 5px);
      pointer-events:none;
    }
    .pv-root .mobile-page-segment{
      flex:1 1 0; min-width:1px; height:3.5px; border-radius:2px;
      background:color-mix(in srgb, var(--color-od-content-inverse-primary) 65%, transparent);
      transition:height .15s ease-in-out, background-color .15s ease-in-out;
    }
    .pv-root .mobile-page-segment.active{ height:6px; background:var(--color-od-bg-primary); }
    .pv-root .mobile-page-indicator.scrubbing .mobile-page-segment.active{ height:8px; }
    .pv-root .mobile-page-indicator.scrubbing .mobile-page-segment.adjacent{ height:6px; }
    .pv-root .mobile-page-indicator.single{ display:none; }
    .pv-zoom .zoom-bar{ padding:0 16px; }
    .pv-zoom .zoom-scroll{
      flex-direction:row; overflow-x:auto; overflow-y:hidden; scrollbar-width:none;
      touch-action:pan-x pinch-zoom;
    }
    .pv-zoom .zoom-scroll::-webkit-scrollbar{ display:none; }
    .pv-zoom .zoom-scroll.snap{ scroll-snap-type:x mandatory; }
    .pv-zoom .zoom-slot{ flex:0 0 100%; }
    .pv-zoom .zoom-frame{ cursor:default; }
    .pv-zoom .zoom-scrubber{
      top:auto; bottom:calc(16px + env(safe-area-inset-bottom, 0px)); left:0; right:0;
      width:100%; height:64px; padding:12px; flex-direction:row; align-items:center;
      overflow-x:auto; overflow-y:hidden; overscroll-behavior-x:contain; touch-action:pan-x pinch-zoom;
    }
    .pv-zoom .zoom-thumb{ flex-basis:calc(var(--zoom-thumb-width) * .65); }
    .pv-zoom .zoom-thumb.outer{ flex-basis:calc(var(--zoom-thumb-width) * .75); }
    .pv-zoom .zoom-thumb.near{ flex-basis:calc(var(--zoom-thumb-width) * .9); }
    .pv-zoom .zoom-thumb.previewing,
    .pv-zoom .zoom-thumb.active{ flex-basis:var(--zoom-thumb-width); }
    .pv-zoom .zoom-thumb,
    .pv-zoom .zoom-thumb.outer,
    .pv-zoom .zoom-thumb.near,
    .pv-zoom .zoom-thumb.previewing,
    .pv-zoom .zoom-thumb.active{ height:var(--zoom-thumb-height); }
    .pv-zoom .zoom-thumb-preview{ display:none; }
    .pv-zoom .zoom-details{ display:none; }
    .pv-zoom .zoom-mobile-details{
      position:absolute; top:calc(var(--zoom-y) + var(--photo-h) + ${MOBILE_ZOOM_DETAILS_GAP}px); left:16px; right:16px; z-index:40;
      pointer-events:none; opacity:0;
      display:flex; flex-direction:column; align-items:center; gap:2px;
      color:var(--color-od-content-inverse-primary); text-align:center;
    }
    .pv-zoom .zoom-details-address{ font-size:var(--text-label-xs); line-height:var(--text-label-xs--line-height); }
    .pv-zoom .zoom-details-facts{ font-size:var(--text-body-xs); line-height:var(--text-body-xs--line-height); }
  }
  @media (prefers-reduced-motion: reduce){
    .pv-zoom .zoom-thumb, .pv-zoom .zoom-thumb-frame, .pv-zoom .zoom-thumb-preview{ transition:none; }
  }
`;

/* PAD is every gap in the scrubber; a boundary between inside and outside measures PAD + PAD. */
const PAD = 8;
const THUMB_ASPECT = 4 / 3;
const COVER_IN = 0.05;
const COVER_FULL = 0.8;
const COVER_MID = (COVER_IN + COVER_FULL) / 2;
const COVER_HYST = 0.05;
const SETTLE_MS = 160;
const LIVE_IN_MS = 130;
const SIDE_ROOM = 22 + 38 * 2 + 10 + 28;
const SCRUBBER_EDGE = 24;

function fallback(w: number, h: number) {
  return (
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"/>`)
  );
}
function makeImg(
  src: string,
  alt: string,
  w: number,
  h: number,
  srcSet?: string,
  sizes?: string,
  loading?: 'eager' | 'lazy',
) {
  const img = document.createElement('img');
  if (loading) img.loading = loading;
  img.src = src;
  if (srcSet) {
    img.srcset = srcSet;
    if (sizes) img.sizes = sizes;
  }
  img.alt = alt;
  img.draggable = false;
  img.addEventListener(
    'error',
    () => {
      img.removeAttribute('srcset');
      img.style.backgroundColor = 'var(--color-od-bg-tertiary)';
      img.src = fallback(w, h);
    },
    { once: true },
  );
  return img;
}
function smoothstep(e0: number, e1: number, x: number) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function el(scope: ParentNode, key: string): HTMLElement {
  const node = scope.querySelector(`[data-pv="${key}"]`);
  if (!(node instanceof HTMLElement)) throw new Error(`PhotoViewer: missing [data-pv="${key}"]`);
  return node;
}

type Range = [number, number];
type Rect = { x: number; y: number; w: number; h: number };
type DragState = {
  id: number;
  startX: number;
  startScroll: number;
  gain: number;
  slop: number;
  lastX: number;
  lastT: number;
  v: number;
  thumbIdx: number;
  moved: boolean;
};

/* All viewer behaviour. Runs once per mounted root; returns a teardown. */
const VIEWER_IMAGE_SIZES = '(max-width: 767px) 100vw, 50vw';

function isTextEntry(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

function mountViewer(
  root: HTMLElement,
  zoom: HTMLElement,
  GRID_PHOTOS: ViewerPhoto[],
  ALL_PHOTOS: ViewerPhoto[],
  cfg: ViewerConfig,
) {
  const viewer = el(root, 'viewer');
  const stage = el(root, 'stage');
  const track = el(root, 'track');
  const pill = el(root, 'pill');
  const clip = el(root, 'clip');
  const rail = el(root, 'rail');
  const thumbsEl = el(root, 'thumbs');
  const winEl = el(root, 'window');
  const mobileIndicator = el(root, 'mobileIndicator');
  const counter = el(root, 'counter');
  const showAllButton = root.querySelector('[data-pv="showAll"]');
  const inert = !!root.closest('[inert]');

  const cells: { colIndex: number; el: HTMLElement }[] = [];
  const columns: { el: HTMLElement; photos: number[] }[] = [];
  const thumbEls: HTMLElement[] = [];
  const mobilePageSegments = Array.from(
    mobileIndicator.querySelectorAll<HTMLElement>('.mobile-page-segment'),
  );
  let tW = 40;
  let tH = 30;
  let maxActive = 1;
  let colGeom: { l: number; w: number }[] = [];
  let colIn: boolean[] = [];
  let stageIsPhone = isPhone(root.clientWidth);
  let stagePhotos = stageIsPhone ? ALL_PHOTOS : GRID_PHOTOS;

  function buildStage() {
    track.innerHTML = '';
    cells.length = 0;
    columns.length = 0;
    let i = 0;
    while (i < stagePhotos.length) {
      const col = document.createElement('div');
      col.className = 'col';
      const group: number[] = [];
      if (stageIsPhone || stagePhotos[i].wide || i === stagePhotos.length - 1) {
        group.push(i);
        i++;
      } else {
        group.push(i);
        i++;
        if (i < stagePhotos.length && !stagePhotos[i].wide) {
          group.push(i);
          i++;
        }
      }
      group.forEach((pi) => {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.appendChild(
          makeImg(
            stagePhotos[pi].src,
            stagePhotos[pi].alt,
            1600,
            1067,
            stagePhotos[pi].srcSet,
            VIEWER_IMAGE_SIZES,
            stageIsPhone && pi > 0 ? 'lazy' : undefined,
          ),
        );
        col.appendChild(cell);
        cells[pi] = { colIndex: columns.length, el: cell };
      });
      columns.push({ el: col, photos: group });
      track.appendChild(col);
    }
    sizeColumns();
  }

  function sizeColumns() {
    const H = stage.clientHeight;
    const frame = geometry(root.clientWidth, cfg.grid);
    const initialSupportingWidth = (stage.clientWidth - frame.heroW - 2 * cfg.grid.gap) / 2;
    columns.forEach((col, ci) => {
      const n = col.photos.length;
      if (ci === 0 && n === 1) {
        col.el.style.width = frame.heroW + 'px';
        return;
      }
      if ((ci === 1 || ci === 2) && n === 2) {
        col.el.style.width = initialSupportingWidth + 'px';
        return;
      }
      const rowH = n === 1 ? H : (H - cfg.grid.gap) / 2;
      const aspect = n === 1 ? cfg.columnsAfter.wideAspect : cfg.columnsAfter.stackedAspect;
      col.el.style.width = Math.round(rowH * aspect) + 'px';
    });
    measureColumns();
  }

  function measureColumns() {
    colGeom = columns.map((c) => ({ l: c.el.offsetLeft, w: c.el.offsetWidth }));
    if (colIn.length !== colGeom.length) colIn = colGeom.map(() => false);
  }

  function buildThumbs() {
    thumbsEl.innerHTML = '';
    thumbEls.length = 0;
    stagePhotos.forEach((p) => {
      const t = document.createElement('div');
      t.className = 'thumb';
      t.appendChild(makeImg(p.thumb || p.src, p.alt, 120, 90, undefined, undefined, 'lazy'));
      thumbsEl.appendChild(t);
      thumbEls.push(t);
    });
    sizeThumbs();
  }

  function trackWidth() {
    const n = stagePhotos.length;
    return n * tW + (n + 3) * PAD;
  }
  let viewW = 0;

  function sizeThumbs() {
    const n = stagePhotos.length;
    if (n === 0) return;
    const sideRoom = isPhone(viewer.clientWidth) ? SCRUBBER_EDGE : SIDE_ROOM;
    const avail = Math.min(viewer.clientWidth - 48 - sideRoom * 2, 1180);
    maxActive = sweepExtremes().maxActive;
    const nRef = Math.min(n, 22);
    const w = (avail - (nRef + 3) * PAD) / nRef;
    tW = Math.max(28, Math.min(Math.round(w / 4) * 4, 44));
    tH = tW / THUMB_ASPECT;
    root.style.setProperty('--pill-h', tH + PAD * 2 + 'px');
    viewW = Math.min(trackWidth(), viewer.clientWidth - sideRoom * 2, 1240);
    pill.style.width = viewW + 'px';
    rail.style.width = trackWidth() + 'px';
  }

  function sizeMobileIndicator() {
    const count = stagePhotos.length;
    if (count <= 1) return;
    const availableWidth = Math.max(0, stage.clientWidth - 100);
    const ratioBasedSpacing = availableWidth / (count / 0.5 + count - 1);
    const densityAwareSpacing = Math.min(5, Math.max(2, ratioBasedSpacing));
    const maximumSpacingWithoutOverflow = Math.max(
      0,
      (availableWidth - count) / Math.max(1, count - 1),
    );
    mobileIndicator.style.setProperty(
      '--indicator-gap',
      Math.min(densityAwareSpacing, maximumSpacingWithoutOverflow) + 'px',
    );
  }

  function syncMobileIndicator(index: number) {
    mobilePageSegments.forEach((segment, segmentIndex) => {
      segment.classList.toggle('active', segmentIndex === index);
      segment.classList.toggle('adjacent', Math.abs(segmentIndex - index) === 1);
    });
  }

  function layoutThumbs(av: number[], disc: number[], range: Range) {
    const n = thumbEls.length;
    let x = PAD * (1 + av[n - 1]);
    const xs = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) {
      if (i > 0) x += PAD + PAD * Math.abs(av[i] - av[i - 1]);
      const t = thumbEls[i];
      xs[i] = x;
      t.style.width = tW + 'px';
      t.style.height = tH + 'px';
      t.style.transform = 'translate(' + x.toFixed(2) + 'px, -50%)';
      t.style.opacity = (0.72 + 0.28 * av[i]).toFixed(3);
      t.classList.toggle('active', disc[i] === 1);
      x += tW;
    }
    const xAt = (f: number) => {
      f = Math.max(0, Math.min(n - 1, f));
      const i0 = Math.max(0, Math.min(n - 1, Math.floor(f)));
      const t = f - i0;
      const nxt = i0 + 1 < n ? xs[i0 + 1] : xs[i0] + tW + PAD;
      return xs[i0] + t * (nxt - xs[i0]);
    };
    const L = xAt(range[0]) - PAD;
    const R = xAt(range[1]) + tW + PAD;
    winEl.style.left = L.toFixed(2) + 'px';
    winEl.style.width = Math.max(0, R - L).toFixed(2) + 'px';
    winEl.style.height = tH + PAD * 2 + 'px';
    winEl.style.top = '0px';
    const maxOff = Math.max(0, trackWidth() - viewW);
    const off = Math.max(0, Math.min(maxOff, (L + R) / 2 - viewW / 2));
    rail.style.transform = 'translateX(' + (-off).toFixed(2) + 'px)';
    clip.style.setProperty('--fadeL', Math.min(24, off).toFixed(1) + 'px');
    clip.style.setProperty('--fadeR', Math.min(24, maxOff - off).toFixed(1) + 'px');
  }

  function coverAt(s: number) {
    const vw = stage.clientWidth;
    const out = Array.from({ length: colGeom.length }, () => 0);
    for (let i = 0; i < colGeom.length; i++) {
      const g = colGeom[i];
      const ov = Math.max(0, Math.min(s + vw, g.l + g.w) - Math.max(s, g.l));
      out[i] = ov / Math.min(g.w, vw);
    }
    return out;
  }
  function activationsAt(s: number) {
    const cov = coverAt(s);
    const out = Array.from({ length: stagePhotos.length }, () => 0);
    for (let ci = 0; ci < columns.length; ci++) {
      const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
      for (const pi of columns[ci].photos) out[pi] = a;
    }
    return out;
  }
  function rangeAt(s: number): Range {
    if (columns.length === 0) return [0, 0];
    const cov = coverAt(s);
    let first = -1;
    let last = -1;
    let best = 0;
    for (let ci = 0; ci < cov.length; ci++) {
      if (cov[ci] > cov[best]) best = ci;
      if (cov[ci] >= COVER_MID) {
        for (const pi of columns[ci].photos) {
          if (first < 0) first = pi;
          last = pi;
        }
      }
    }
    if (first < 0) {
      const p = columns[best].photos;
      return [p[0], p[p.length - 1]];
    }
    return [first, last];
  }
  function visibleRange() {
    return rangeAt(stage.scrollLeft);
  }
  function sweepExtremes() {
    const max = maxScroll();
    let found = 1;
    const stride = Math.max(8, Math.round(max / 400));
    for (let s = 0; s <= max + stride; s += stride) {
      const [a, b] = rangeAt(Math.min(s, max));
      if (b - a + 1 > found) found = b - a + 1;
    }
    return { maxActive: found };
  }

  let liveMode = false;
  let liveT0 = 0;
  let entryAv: number[] | null = null;
  let entryRange: Range | null = null;
  let fromAv: number[] | null = null;
  let fromRange: Range | null = null;
  let rTo: Range | null = null;
  let blend = 1;
  let blendRaf: number | null = null;
  let lastAv: number[] | null = null;
  let lastRange: Range | null = null;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function stopBlend() {
    if (blendRaf) {
      cancelAnimationFrame(blendRaf);
      blendRaf = null;
    }
  }
  const lerpAv = (a: number[], b: number[], t: number) => b.map((v, i) => a[i] + (v - a[i]) * t);
  const lerpRange = (a: Range, b: Range, t: number): Range => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
  ];
  function avOfRange(r: Range) {
    const lo = r[0] - 0.5;
    const hi = r[1] + 0.5;
    const n = stagePhotos.length;
    const out = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) out[i] = Math.max(0, Math.min(hi, i + 0.5) - Math.max(lo, i - 0.5));
    return out;
  }
  function rangeOfAv(av: number[]): Range {
    let sum = 0;
    let wsum = 0;
    for (let i = 0; i < av.length; i++) {
      sum += av[i];
      wsum += i * av[i];
    }
    if (sum <= 0) return [0, 0];
    const centre = wsum / sum;
    return [centre - (sum - 1) / 2, centre + (sum - 1) / 2];
  }
  function rangeFromColIn(): Range {
    let first = -1;
    let last = -1;
    for (let ci = 0; ci < columns.length; ci++) {
      if (!colIn[ci]) continue;
      for (const pi of columns[ci].photos) {
        if (first < 0) first = pi;
        last = pi;
      }
    }
    return first < 0 ? rangeAt(stage.scrollLeft) : [first, last];
  }
  function beginLive() {
    stopBlend();
    entryAv = lastAv ? lastAv.slice() : activationsAt(stage.scrollLeft);
    entryRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = true;
    liveT0 = reducedMotion.matches ? 0 : performance.now();
  }
  function endLive(dur = SETTLE_MS) {
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = false;
    rTo = rangeFromColIn();
    blend = 0;
    if (reducedMotion.matches) {
      blend = 1;
      fromAv = null;
      fromRange = null;
      syncScrubber();
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      blend = 1 - Math.pow(1 - p, 3);
      syncScrubber();
      if (p < 1) blendRaf = requestAnimationFrame(tick);
      else {
        blendRaf = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    blendRaf = requestAnimationFrame(tick);
  }

  function syncScrubber() {
    if (stagePhotos.length === 0) return;
    const cov = coverAt(stage.scrollLeft);
    const n = stagePhotos.length;
    let av: number[];
    let shown: number[];
    let range: Range;
    if (liveMode) {
      for (let ci = 0; ci < cov.length; ci++) {
        colIn[ci] = cov[ci] >= (colIn[ci] ? COVER_MID - COVER_HYST : COVER_MID + COVER_HYST);
      }
      if (!colIn.some(Boolean)) {
        let best = 0;
        for (let ci = 1; ci < cov.length; ci++) if (cov[ci] > cov[best]) best = ci;
        colIn[best] = true;
      }
      const cont = Array.from({ length: n }, () => 0);
      shown = Array.from({ length: n }, () => 0);
      columns.forEach((c, ci) => {
        const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
        const d = colIn[ci] ? 1 : 0;
        for (const pi of c.photos) {
          cont[pi] = a;
          shown[pi] = d;
        }
      });
      const p = Math.min(1, (performance.now() - liveT0) / LIVE_IN_MS);
      const e = 1 - Math.pow(1 - p, 3);
      const contRange = rangeOfAv(cont);
      av = e >= 1 || !entryAv ? cont : lerpAv(entryAv, cont, e);
      range = e >= 1 || !entryRange ? contRange : lerpRange(entryRange, contRange, e);
    } else {
      const to = rTo || rangeAt(stage.scrollLeft);
      const target = avOfRange(to);
      av = !fromAv || blend >= 1 ? target : lerpAv(fromAv, target, blend);
      range = !fromRange || blend >= 1 ? to : lerpRange(fromRange, to, blend);
      shown = Array.from({ length: n }, () => 0);
      for (let i = Math.round(to[0]); i <= Math.round(to[1]); i++) shown[i] = 1;
      for (let ci = 0; ci < columns.length; ci++) colIn[ci] = shown[columns[ci].photos[0]] === 1;
    }
    layoutThumbs(av, shown, range);
    lastAv = av.slice();
    lastRange = [range[0], range[1]];
    let first = -1;
    let last = -1;
    for (let i = 0; i < n; i++)
      if (shown[i]) {
        if (first < 0) first = i;
        last = i;
      }
    counter.textContent =
      first === last ? `${first + 1} of ${n}` : `${first + 1}–${last + 1} of ${n}`;
    syncMobileIndicator(Math.max(0, first));
  }

  function maxScroll() {
    return Math.max(0, stage.scrollWidth - stage.clientWidth);
  }
  let anim: { raf: number } | null = null;
  let navTarget: number | null = null;
  function stopAnim() {
    if (anim) {
      cancelAnimationFrame(anim.raf);
      anim = null;
    }
  }
  function goTo(target: number, smooth = true, dur = 380) {
    stopAnim();
    target = Math.max(0, Math.min(target, maxScroll()));
    navTarget = target;
    if (!smooth || reducedMotion.matches) {
      stopBlend();
      liveMode = false;
      stage.scrollLeft = target;
      navTarget = null;
      fromAv = null;
      fromRange = null;
      rTo = rangeAt(target);
      blend = 1;
      syncScrubber();
      return;
    }
    const from = stage.scrollLeft;
    const dist = target - from;
    if (Math.abs(dist) < 1) {
      navTarget = null;
      endLive();
      return;
    }
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(from);
    liveMode = false;
    rTo = rangeAt(target);
    blend = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      stage.scrollLeft = from + dist * e;
      blend = e;
      syncScrubber();
      if (p < 1 && anim) anim.raf = requestAnimationFrame(tick);
      else {
        anim = null;
        navTarget = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    anim = { raf: requestAnimationFrame(tick) };
  }
  function scrollToPhoto(idx: number, smooth = true) {
    const cell = cells[idx];
    if (!cell) return;
    goTo(columns[cell.colIndex].el.offsetLeft, smooth);
  }
  function step(dir: number) {
    const cur = navTarget !== null ? navTarget : stage.scrollLeft;
    let target: number;
    if (dir > 0) {
      const nextCol = columns.find((c) => c.el.offsetLeft > cur + 2);
      target = nextCol ? nextCol.el.offsetLeft : maxScroll();
    } else {
      const prevCols = columns.filter((c) => c.el.offsetLeft < cur - 2);
      target = prevCols.length ? prevCols[prevCols.length - 1].el.offsetLeft : 0;
    }
    goTo(target);
  }

  function focusableZoomElements() {
    return Array.from(
      zoom.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => {
      if (element.tabIndex < 0 || element.closest('[hidden], [inert]')) return false;
      for (let node: HTMLElement | null = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (node === zoom) break;
      }
      return true;
    });
  }

  const onKey = (e: KeyboardEvent) => {
    const target = e.target;
    const inZoom = target instanceof Node && zoom.contains(target);
    const inViewer = target instanceof Node && root.contains(target);
    const atPage = target === document.body || target === document.documentElement;
    if (zoomed >= 0) {
      if (e.key === 'Tab') {
        const focusable = focusableZoomElements();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (
          first &&
          last &&
          (!inZoom || target === zoom || (e.shiftKey ? target === first : target === last))
        ) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus({ preventScroll: true });
        }
        return;
      }
      if (!inZoom && !atPage) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeZoom();
      } else if (isTextEntry(target)) {
        return;
      } else if (e.key === (isPhone(window.innerWidth) ? 'ArrowRight' : 'ArrowDown')) {
        e.preventDefault();
        zoomStep(1);
      } else if (e.key === (isPhone(window.innerWidth) ? 'ArrowLeft' : 'ArrowUp')) {
        e.preventDefault();
        zoomStep(-1);
      }
      return;
    }
    if (isTextEntry(target)) return;
    if (stagePhotos.length === 0 || (!inViewer && !atPage)) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      step(-1);
    }
  };
  if (!inert) window.addEventListener('keydown', onKey);

  let drag: DragState | null = null;
  function scrubGain() {
    const nominalPlate = maxActive * tW + (maxActive + 1) * PAD;
    return maxScroll() / Math.max(1, trackWidth() - nominalPlate);
  }
  const onDown = (e: PointerEvent) => {
    e.preventDefault();
    if (anim) {
      stopAnim();
      navTarget = null;
      endLive();
    }
    const thumb = e.target instanceof Element ? e.target.closest('.thumb') : null;
    drag = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      gain: scrubGain(),
      slop: e.pointerType === 'touch' ? 10 : 4,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      thumbIdx: thumb instanceof HTMLElement ? thumbEls.indexOf(thumb) : -1,
      moved: false,
    };
    pill.setPointerCapture(e.pointerId);
  };
  pill.addEventListener('pointerdown', onDown);
  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (!drag.moved) {
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) < drag.slop) return;
      drag.moved = true;
      stopAnim();
      navTarget = null;
      drag.startX += Math.sign(dx) * drag.slop;
      beginLive();
      pill.classList.add('scrubbing');
      document.body.style.cursor = 'grabbing';
    }
    const now = performance.now();
    const dt = Math.max(1, now - drag.lastT);
    drag.v = 0.75 * drag.v + 0.25 * ((e.clientX - drag.lastX) / dt);
    drag.lastX = e.clientX;
    drag.lastT = now;
    const s = drag.startScroll + (e.clientX - drag.startX) * drag.gain;
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      drag.startX = e.clientX;
      drag.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  pill.addEventListener('pointermove', onMove);
  function trackJump(clientX: number) {
    let best = 0;
    let bd = Infinity;
    thumbEls.forEach((t, i) => {
      const r = t.getBoundingClientRect();
      const d = Math.abs(clientX - (r.left + r.width / 2));
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    scrollToPhoto(best);
  }
  function settleSnap(d: { v: number; gain: number }) {
    const here = stage.scrollLeft;
    const vScroll = (d.v || 0) * d.gain;
    const bounds = columns.map((c) => Math.min(c.el.offsetLeft, maxScroll())).concat([maxScroll()]);
    let bL = 0;
    let bR = maxScroll();
    for (const b of bounds) {
      if (b <= here && b > bL) bL = b;
      if (b >= here && b < bR) bR = b;
    }
    const proj = here + vScroll * 150;
    const target = Math.abs(proj - bL) <= Math.abs(proj - bR) ? bL : bR;
    goTo(target, true, 240);
  }
  function endDrag(e?: PointerEvent) {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag;
    drag = null;
    pill.classList.remove('scrubbing');
    document.body.style.cursor = '';
    const isUp = !!e && e.type === 'pointerup';
    if (!d.moved) {
      if (isUp && d.thumbIdx >= 0) scrollToPhoto(d.thumbIdx);
      else if (isUp && e) trackJump(e.clientX);
      else endLive();
    } else {
      settleSnap(d);
    }
  }
  pill.addEventListener('pointerup', endDrag);
  pill.addEventListener('pointercancel', endDrag);

  /* ---------------- stage swipe ---------------- */
  // A finger swipe (or mouse drag) across the photos moves the grid 1:1 and snaps to the nearest
  // column on release. The stage allows vertical pans through, so the page still scrolls.
  type SwipeState = {
    id: number;
    startX: number;
    startScroll: number;
    lastX: number;
    lastT: number;
    v: number;
    slop: number;
    moved: boolean;
  };
  type MobileScrubState = {
    id: number;
    startX: number;
    startIndex: number;
    currentIndex: number;
    stripWidth: number;
  };
  let swipe: SwipeState | null = null;
  let mobileScrub: MobileScrubState | null = null;
  let mobileScrubTimer: ReturnType<typeof setTimeout> | undefined;
  let suppressCellClick = false;
  const clearMobileScrubTimer = () => {
    clearTimeout(mobileScrubTimer);
    mobileScrubTimer = undefined;
  };
  const setMobileScrubPresentation = (active: boolean) => {
    stage.classList.toggle('mobile-scrubbing', active);
    mobileIndicator.classList.toggle('scrubbing', active);
  };
  const beginMobileScrub = (pointerId: number, clientX: number) => {
    if (!stageIsPhone || stagePhotos.length <= 1 || !swipe || swipe.id !== pointerId) return;
    const startIndex = Math.max(0, Math.min(stagePhotos.length - 1, visibleRange()[0]));
    stopAnim();
    navTarget = null;
    swipe = null;
    mobileScrub = {
      id: pointerId,
      startX: clientX,
      startIndex,
      currentIndex: startIndex,
      stripWidth: stage.clientWidth,
    };
    setMobileScrubPresentation(true);
    if (typeof stage.setPointerCapture === 'function') stage.setPointerCapture(pointerId);
    navigator.vibrate?.(10);
  };
  const endMobileScrub = (pointerId?: number) => {
    if (!mobileScrub || (pointerId != null && mobileScrub.id !== pointerId)) return false;
    mobileScrub = null;
    setMobileScrubPresentation(false);
    suppressCellClick = true;
    setTimeout(() => {
      suppressCellClick = false;
    }, 0);
    return true;
  };
  const onStageDown = (e: PointerEvent) => {
    if (zoomed >= 0 || swipe || drag || mobileScrub) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (
      e.target instanceof Element &&
      e.target.closest('.hero-overlay, .gallery-media-actions, .show-all-photos')
    )
      return;
    swipe = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      slop: e.pointerType === 'touch' ? 10 : 4,
      moved: false,
    };
    if (stageIsPhone && e.pointerType === 'touch' && stagePhotos.length > 1) {
      clearMobileScrubTimer();
      mobileScrubTimer = setTimeout(
        () => beginMobileScrub(e.pointerId, e.clientX),
        MOBILE_SCRUB_HOLD_MS,
      );
    }
  };
  const onStageMove = (e: PointerEvent) => {
    if (mobileScrub && e.pointerId === mobileScrub.id) {
      e.preventDefault();
      const nextIndex = mobilePhotoScrubIndex({
        count: stagePhotos.length,
        currentX: e.clientX,
        startIndex: mobileScrub.startIndex,
        startX: mobileScrub.startX,
        stripWidth: mobileScrub.stripWidth,
      });
      if (nextIndex === mobileScrub.currentIndex) return;
      mobileScrub.currentIndex = nextIndex;
      scrollToPhoto(nextIndex, false);
      navigator.vibrate?.(3);
      return;
    }
    if (!swipe || e.pointerId !== swipe.id) return;
    if (!swipe.moved) {
      const dx = e.clientX - swipe.startX;
      if (Math.abs(dx) < swipe.slop) return;
      clearMobileScrubTimer();
      swipe.moved = true;
      stopAnim();
      navTarget = null;
      beginLive();
      swipe.startX += Math.sign(dx) * swipe.slop;
      stage.setPointerCapture(e.pointerId);
      stage.classList.add('swiping');
    }
    const now = performance.now();
    const dt = Math.max(1, now - swipe.lastT);
    swipe.v = 0.75 * swipe.v + 0.25 * ((e.clientX - swipe.lastX) / dt);
    swipe.lastX = e.clientX;
    swipe.lastT = now;
    const s = swipe.startScroll - (e.clientX - swipe.startX);
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      swipe.startX = e.clientX;
      swipe.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  const endSwipe = (e: PointerEvent) => {
    clearMobileScrubTimer();
    if (endMobileScrub(e.pointerId)) return;
    if (!swipe || e.pointerId !== swipe.id) return;
    const s = swipe;
    swipe = null;
    stage.classList.remove('swiping');
    if (!s.moved) return;
    // The click that follows a drag must not open the photo under the pointer.
    suppressCellClick = true;
    setTimeout(() => {
      suppressCellClick = false;
    }, 0);
    // Finger velocity runs opposite to scroll velocity.
    settleSnap({ v: s.v, gain: -1 });
  };
  stage.addEventListener('pointerdown', onStageDown);
  stage.addEventListener('pointermove', onStageMove);
  stage.addEventListener('pointerup', endSwipe);
  stage.addEventListener('pointercancel', endSwipe);
  const onStageTouchMove = (event: TouchEvent) => {
    if (mobileScrub) event.preventDefault();
  };
  stage.addEventListener('touchmove', onStageTouchMove, { passive: false });
  const onStageContextMenu = (event: MouseEvent) => {
    if (stageIsPhone) event.preventDefault();
  };
  stage.addEventListener('contextmenu', onStageContextMenu);

  /* ---------------- zoom ---------------- */
  const zoomBackdrop = el(zoom, 'zoomBackdrop');
  const zoomScroll = el(zoom, 'zoomScroll');
  zoomScroll.innerHTML = '';
  const zoomDetails = el(zoom, 'zoomDetails');
  const zoomMobileDetails = el(zoom, 'zoomMobileDetails');
  const zoomBar = el(zoom, 'zoomBar');
  const zoomScrubber = el(zoom, 'zoomScrubber');
  zoomScrubber.innerHTML = '';
  const thumbPreview = el(zoom, 'thumbPreview');
  const previewImageNode = thumbPreview.querySelector('img');
  if (!previewImageNode) throw new Error('PhotoViewer: thumbnail preview image missing');
  const previewImage = previewImageNode;
  const previewCount = el(zoom, 'previewCount');
  let previewIndex = -1;
  const slots: HTMLElement[] = [];
  const zoomThumbButtons: HTMLButtonElement[] = [];
  let lightboxContentReady = false;
  function ensureLightboxContent() {
    if (lightboxContentReady) return;
    lightboxContentReady = true;
    ALL_PHOTOS.forEach((photo, index) => {
      const slot = document.createElement('div');
      slot.className = 'zoom-slot';
      const frame = document.createElement('div');
      frame.className = 'zoom-frame';
      const image = makeImg(photo.src, photo.alt, 1600, 1067, undefined, undefined, 'lazy');
      image.decoding = 'async';
      const photoCounter = document.createElement('div');
      photoCounter.className = 'counter';
      photoCounter.textContent = `${index + 1} / ${ALL_PHOTOS.length}`;
      photoCounter.setAttribute('aria-hidden', 'true');
      photoCounter.hidden = !image.complete || image.naturalWidth === 0;
      const photoDetails = document.importNode(zoomDetails, true);
      photoDetails.removeAttribute('id');
      photoDetails.removeAttribute('data-pv');
      photoDetails.setAttribute('aria-hidden', 'true');
      photoDetails.className = 'zoom-details';
      photoDetails.hidden = photoCounter.hidden;
      image.addEventListener(
        'load',
        () => {
          photoCounter.hidden = false;
          photoDetails.hidden = false;
        },
        { once: true },
      );
      frame.append(image, photoCounter, photoDetails);
      slot.appendChild(frame);
      zoomScroll.appendChild(slot);
      slots.push(slot);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zoom-thumb';
      button.dataset.photoIndex = String(index);
      button.setAttribute('aria-label', `View photo ${index + 1}`);
      const thumbnail = makeImg(
        photo.thumb || photo.src,
        '',
        120,
        90,
        undefined,
        undefined,
        'lazy',
      );
      thumbnail.decoding = 'async';
      const thumbnailFrame = document.createElement('span');
      thumbnailFrame.className = 'zoom-thumb-frame';
      thumbnailFrame.appendChild(thumbnail);
      button.appendChild(thumbnailFrame);
      zoomScrubber.appendChild(button);
      zoomThumbButtons.push(button);
    });
  }
  const zoomChrome = [zoomBackdrop, zoomScroll, zoomBar, zoomScrubber, zoomMobileDetails];
  let zoomed = -1;
  let zoomAnim: ReturnType<typeof animate> | null = null;
  let zoomTransitionId = 0;
  let closing = false;
  let zoomScrollAnim: ReturnType<typeof animate> | null = null;
  let pageOverflow: string | null = null;
  let focusBeforeZoom: HTMLElement | null = null;
  // While a photo is open the page behind must not take wheel or scrollbar input; the zoom
  // stack scrolls instead and shows its own scrollbar in the same place.
  function lockPage() {
    if (pageOverflow !== null) return;
    pageOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockPage() {
    if (pageOverflow === null) return;
    document.documentElement.style.overflow = pageOverflow;
    pageOverflow = null;
  }
  function focusZoom(trigger: HTMLElement | null) {
    focusBeforeZoom = trigger;
    zoom.focus({ preventScroll: true });
  }
  function restoreFocus() {
    const target = focusBeforeZoom;
    focusBeforeZoom = null;
    if (target?.isConnected) {
      const clearRestoredFocus = () => target.classList.remove('pv-restored-focus');
      target.classList.add('pv-restored-focus');
      target.addEventListener('blur', clearRestoredFocus, { once: true });
      target.focus({ preventScroll: true });
      if (document.activeElement === target) return;
      target.removeEventListener('blur', clearRestoredFocus);
      clearRestoredFocus();
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && zoom.contains(active)) active.blur();
  }

  function photoInsets() {
    return isPhone(window.innerWidth)
      ? { left: 0, right: 0 }
      : { left: cfg.zoom.sideInset, right: cfg.zoom.sideInset };
  }
  function fullRect(): Rect {
    const inset = photoInsets();
    const scrollbar = zoomScroll.offsetWidth - zoomScroll.clientWidth;
    const availableWidth = Math.max(0, window.innerWidth - scrollbar - inset.left - inset.right);
    const availableHeight = slotHeight();
    const ratio = ZOOM_PHOTO_RATIO;
    const w = Math.min(availableWidth, availableHeight * ratio);
    const h = w / ratio;
    return {
      x: inset.left + (availableWidth - w) / 2,
      y: zoomPhotoTop() + (availableHeight - h) / 2,
      w,
      h,
    };
  }
  function zoomPhotoTop() {
    return isPhone(window.innerWidth) ? BAR_H : ZOOM_PHOTO_TOP;
  }
  function slotHeight() {
    const bottom = isPhone(window.innerWidth)
      ? zoomScrubber.offsetTop - zoomMobileDetails.offsetHeight - MOBILE_ZOOM_DETAILS_GAP * 2
      : window.innerHeight - ZOOM_PHOTO_BOTTOM;
    return Math.max(1, bottom - zoomPhotoTop());
  }
  function zoomScrollProperty() {
    return isPhone(window.innerWidth) ? 'scrollLeft' : 'scrollTop';
  }
  function setChrome(opacity: number) {
    for (const node of zoomChrome) node.style.opacity = opacity.toFixed(3);
  }
  function slotPitch() {
    return (isPhone(window.innerWidth) ? window.innerWidth : slotHeight()) + cfg.zoom.stackGap;
  }
  function layoutScroller() {
    // Size it first so fullRect() can see whether a scrollbar is being drawn.
    zoomScroll.style.transform = `translate(0px, ${zoomPhotoTop()}px)`;
    zoomScroll[isPhone(window.innerWidth) ? 'scrollTop' : 'scrollLeft'] = 0;
    zoomScrubber[isPhone(window.innerWidth) ? 'scrollTop' : 'scrollLeft'] = 0;
    zoomScroll.style.height = slotHeight() + 'px';
    const r = fullRect();
    zoom.style.setProperty('--slot-h', slotHeight() + 'px');
    zoom.style.setProperty('--zoom-x', r.x + 'px');
    zoom.style.setProperty('--zoom-y', r.y + 'px');
    zoom.style.setProperty('--photo-w', r.w + 'px');
    zoom.style.setProperty('--photo-h', r.h + 'px');
    zoom.style.setProperty('--zoom-gap', cfg.zoom.stackGap + 'px');
    zoomScroll.classList.toggle('snap', cfg.zoom.snap);
    updateThumbFade();
  }
  function stopZoomScroll(restoreSnap = true) {
    zoomScrollAnim?.stop();
    zoomScrollAnim = null;
    if (restoreSnap) zoomScroll.classList.toggle('snap', cfg.zoom.snap);
  }
  function scrollToSlot(idx: number, smooth: boolean) {
    const scrollProperty = zoomScrollProperty();
    const from = zoomScroll[scrollProperty];
    stopZoomScroll(false);
    // Native snapping must not compete with the timed photo transition.
    zoomScroll.classList.remove('snap');
    const target = idx * slotPitch();
    if (!smooth || reducedMotion.matches || Math.abs(from - target) < 1) {
      zoomScroll.scrollTo({
        [isPhone(window.innerWidth) ? 'left' : 'top']: target,
        behavior: 'auto',
      });
      zoomScroll.classList.toggle('snap', cfg.zoom.snap);
      return;
    }
    const animation = animate(from, target, {
      ...cfg.zoom.navigate,
      onUpdate: (value: number) => {
        zoomScroll[scrollProperty] = value;
      },
      onComplete: () => {
        if (zoomScrollAnim !== animation) return;
        zoomScroll[scrollProperty] = target;
        stopZoomScroll();
      },
    });
    zoomScrollAnim = animation;
  }
  const onZoomScroll = () => {
    if (zoomed < 0 || closing || zoomScrollAnim || !zoomScroll.classList.contains('live')) return;
    setThumbPreview(-1);
    const idx = Math.max(
      0,
      Math.min(ALL_PHOTOS.length - 1, Math.round(zoomScroll[zoomScrollProperty()] / slotPitch())),
    );
    if (idx !== zoomed) {
      unmagnify(true);
      showZoomPhoto(idx);
    }
  };
  zoomScroll.addEventListener('scroll', onZoomScroll, { passive: true });
  const onZoomPointerDown = () => stopZoomScroll();
  zoomScroll.addEventListener('pointerdown', onZoomPointerDown);
  const onZoomWheel = (event: WheelEvent) => {
    const horizontal = isPhone(window.innerWidth);
    const delta = horizontal ? event.deltaX : event.deltaY;
    const crossDelta = horizontal ? event.deltaY : event.deltaX;
    if (
      zoomed < 0 ||
      !zoomScroll.classList.contains('live') ||
      event.ctrlKey ||
      (!horizontal && event.shiftKey) ||
      Math.abs(crossDelta) > Math.abs(delta) ||
      delta === 0
    ) {
      return;
    }
    stopZoomScroll();
    setThumbPreview(-1);
    if (zoomScrubber.contains(document.activeElement)) zoom.focus({ preventScroll: true });
  };
  zoomScroll.addEventListener('wheel', onZoomWheel, { passive: true });

  function updateThumbFade() {
    const horizontal = isPhone(window.innerWidth);
    const railBounds = zoomScrubber.getBoundingClientRect();
    const length = horizontal ? railBounds.width : railBounds.height;
    if (length === 0) return;
    const viewport = horizontal ? zoomScrubber.clientWidth : zoomScrubber.clientHeight;
    const extent = horizontal ? zoomScrubber.scrollWidth : zoomScrubber.scrollHeight;
    const maxOffset = Math.max(0, extent - viewport);
    const offset = Math.max(0, Math.min(maxOffset, zoomScrubber[zoomScrollProperty()]));
    const hasOverflowBefore = offset > 1;
    const hasOverflowAfter = maxOffset - offset > 1;
    zoomThumbButtons.forEach((button) => {
      const bounds = button.getBoundingClientRect();
      const center = horizontal
        ? bounds.left + bounds.width / 2 - railBounds.left
        : bounds.top + bounds.height / 2 - railBounds.top;
      const position = center / length;
      const startFade = hasOverflowBefore ? (0.35 - position) / 0.35 : 0;
      const endFade = hasOverflowAfter ? (position - 0.65) / 0.35 : 0;
      const progress = Math.max(0, Math.min(1, Math.max(startFade, endFade)));
      button.style.setProperty('--thumb-rail-opacity', (1 - progress * 0.75).toFixed(3));
    });
  }
  function syncZoomScrubber(index: number) {
    zoomThumbButtons.forEach((button, buttonIndex) => {
      const active = buttonIndex === index;
      button.classList.toggle('active', active);
      button.tabIndex = active ? 0 : -1;
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    const active = zoomThumbButtons[index];
    updateThumbFade();
    const horizontal = isPhone(window.innerWidth);
    const viewport = horizontal ? zoomScrubber.clientWidth : zoomScrubber.clientHeight;
    if (!active || viewport === 0) return;
    const extent = horizontal ? zoomScrubber.scrollWidth : zoomScrubber.scrollHeight;
    const offset = horizontal ? active.offsetLeft : active.offsetTop;
    const length = horizontal ? active.offsetWidth : active.offsetHeight;
    const target = Math.max(0, Math.min(extent - viewport, offset - (viewport - length) / 2));
    zoomScrubber.scrollTo({
      [horizontal ? 'left' : 'top']: target,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }
  function thumbnailIndex(target: EventTarget | null) {
    const button = target instanceof Element ? target.closest<HTMLElement>('.zoom-thumb') : null;
    return button?.dataset.photoIndex == null ? -1 : Number(button.dataset.photoIndex);
  }
  function positionThumbPreview() {
    const button = zoomThumbButtons[previewIndex];
    if (!button) return;
    const bounds = button.getBoundingClientRect();
    const railBounds = zoomScrubber.getBoundingClientRect();
    const visible = bounds.bottom >= railBounds.top && bounds.top <= railBounds.bottom;
    thumbPreview.classList.toggle('on', visible);
    if (!visible) return;
    const top = Math.max(
      BAR_H,
      Math.min(
        window.innerHeight - ZOOM_PHOTO_BOTTOM - thumbPreview.offsetHeight,
        bounds.top + bounds.height / 2 - thumbPreview.offsetHeight / 2,
      ),
    );
    thumbPreview.style.top = top + 'px';
  }
  function setThumbPreview(index: number) {
    if (index === previewIndex) return;
    previewIndex = index;
    zoomThumbButtons.forEach((button, buttonIndex) => {
      button.classList.toggle('previewing', buttonIndex === index);
      const distance = Math.abs(buttonIndex - index);
      button.classList.toggle('near', index >= 0 && distance === 1);
      button.classList.toggle('outer', index >= 0 && distance === 2);
    });
    thumbPreview.classList.toggle('on', index >= 0);
    if (index < 0) return;
    previewImage.sizes = '204px';
    previewImage.srcset = ALL_PHOTOS[index].srcSet ?? '';
    previewImage.src = ALL_PHOTOS[index].thumb;
    previewCount.textContent = `${index + 1} / ${ALL_PHOTOS.length}`;
    positionThumbPreview();
  }
  const onThumbPointerMove = (event: PointerEvent) => {
    if (isPhone(window.innerWidth) || event.pointerType === 'touch') return;
    const index = thumbnailIndex(event.target);
    if (index >= 0) setThumbPreview(index);
  };
  const onThumbPointerLeave = () => setThumbPreview(-1);
  const onThumbFocus = (event: FocusEvent) => {
    if (!isPhone(window.innerWidth)) setThumbPreview(thumbnailIndex(event.target));
  };
  const onThumbBlur = (event: FocusEvent) => {
    if (!(event.relatedTarget instanceof Node) || !zoomScrubber.contains(event.relatedTarget)) {
      setThumbPreview(-1);
    }
  };
  zoomScrubber.addEventListener('pointermove', onThumbPointerMove);
  zoomScrubber.addEventListener('pointerleave', onThumbPointerLeave);
  zoomScrubber.addEventListener('focusin', onThumbFocus);
  zoomScrubber.addEventListener('focusout', onThumbBlur);
  const onThumbScroll = () => {
    positionThumbPreview();
    updateThumbFade();
  };
  zoomScrubber.addEventListener('scroll', onThumbScroll, { passive: true });
  const onZoomScrubberClick = (event: MouseEvent) => {
    if (closing) return;
    finishOpening();
    if (event.target === zoomScrubber) {
      closeZoom();
      return;
    }
    const index = thumbnailIndex(event.target);
    if (!Number.isInteger(index) || index < 0 || index >= ALL_PHOTOS.length) return;
    unmagnify(true);
    showZoomPhoto(index);
    if (zoomScroll.classList.contains('live')) scrollToSlot(index, true);
  };
  zoomScrubber.addEventListener('click', onZoomScrubberClick);

  /* ---------------- magnify: click a lightbox photo to look closer inside its frame ---------------- */
  // The frame keeps its size; the photo scales up inside it and the visible part follows the
  // mouse (or a finger drag). Clicking again shows the whole photo.
  const MAGNIFY = 2.5;
  const TOGGLE_MS = 260;
  type Magnified = {
    frame: HTMLElement;
    img: HTMLImageElement;
    w: number;
    h: number;
    tx: number;
    ty: number;
  };
  let magnified: Magnified | null = null;
  let touchPan: {
    id: number;
    startX: number;
    startY: number;
    tx: number;
    ty: number;
    moved: boolean;
  } | null = null;
  // A finger drag that panned the photo must not count as the tap that toggles magnification.
  let panMoved = false;
  let toggleTimer: ReturnType<typeof setTimeout> | undefined;
  function frameOf(target: EventTarget | null) {
    const frame = target instanceof Element ? target.closest('.zoom-frame') : null;
    return frame instanceof HTMLElement ? frame : null;
  }
  function clampPan(m: Magnified, tx: number, ty: number) {
    return {
      tx: Math.min(0, Math.max(m.w - m.w * MAGNIFY, tx)),
      ty: Math.min(0, Math.max(m.h - m.h * MAGNIFY, ty)),
    };
  }
  function applyMagnify() {
    if (!magnified) return;
    magnified.img.style.transform = `translate(${magnified.tx.toFixed(2)}px, ${magnified.ty.toFixed(2)}px) scale(${MAGNIFY})`;
  }
  /** Show the part of the photo under the pointer: left edge of the frame ↔ left edge of the photo, etc. */
  function panToPointer(clientX: number, clientY: number) {
    if (!magnified) return;
    const b = magnified.frame.getBoundingClientRect();
    const fx = Math.max(0, Math.min(1, (clientX - b.left) / b.width));
    const fy = Math.max(0, Math.min(1, (clientY - b.top) / b.height));
    const c = clampPan(
      magnified,
      -fx * (magnified.w * MAGNIFY - magnified.w),
      -fy * (magnified.h * MAGNIFY - magnified.h),
    );
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  }
  function animateToggle(frame: HTMLElement) {
    clearTimeout(toggleTimer);
    frame.classList.add('toggling');
    toggleTimer = setTimeout(() => frame.classList.remove('toggling'), TOGGLE_MS);
  }
  function magnifyAt(frame: HTMLElement, clientX: number, clientY: number) {
    const img = frame.querySelector('img');
    if (!img) return;
    const b = frame.getBoundingClientRect();
    magnified = { frame, img, w: b.width, h: b.height, tx: 0, ty: 0 };
    frame.classList.add('magnified');
    animateToggle(frame);
    panToPointer(clientX, clientY);
  }
  function unmagnify(instant = false) {
    if (!magnified) return;
    const { img, frame } = magnified;
    magnified = null;
    touchPan = null;
    frame.classList.remove('magnified', 'panning');
    if (instant) {
      clearTimeout(toggleTimer);
      frame.classList.remove('toggling');
      img.style.transition = 'none';
      img.style.transform = '';
      void img.offsetWidth;
      img.style.transition = '';
    } else {
      animateToggle(frame);
      img.style.transform = '';
    }
  }
  const onMagnifyMove = (e: PointerEvent) => {
    if (!magnified) return;
    if (e.pointerType === 'mouse') {
      if (frameOf(e.target) === magnified.frame) panToPointer(e.clientX, e.clientY);
      return;
    }
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const dx = e.clientX - touchPan.startX;
    const dy = e.clientY - touchPan.startY;
    if (!touchPan.moved) {
      if (Math.hypot(dx, dy) < 4) return;
      touchPan.moved = true;
      magnified.frame.classList.add('panning');
      magnified.img.setPointerCapture(e.pointerId);
    }
    const c = clampPan(magnified, touchPan.tx + dx, touchPan.ty + dy);
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  };
  const onMagnifyDown = (e: PointerEvent) => {
    if (!magnified || e.pointerType === 'mouse' || frameOf(e.target) !== magnified.frame) return;
    touchPan = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      tx: magnified.tx,
      ty: magnified.ty,
      moved: false,
    };
  };
  const endTouchPan = (e: PointerEvent) => {
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const moved = touchPan.moved;
    touchPan = null;
    magnified?.frame.classList.remove('panning');
    if (moved) {
      panMoved = true;
      setTimeout(() => {
        panMoved = false;
      }, 0);
    }
  };
  zoomScroll.addEventListener('pointerdown', onMagnifyDown);
  zoomScroll.addEventListener('pointermove', onMagnifyMove);
  zoomScroll.addEventListener('pointerup', endTouchPan);
  zoomScroll.addEventListener('pointercancel', endTouchPan);
  function applyZoomStyle() {
    const inset = photoInsets();
    zoom.style.setProperty('--zoom-left-inset', inset.left + 'px');
    zoom.style.setProperty('--zoom-right-inset', inset.right + 'px');
    zoom.style.setProperty('--zoom-x', fullRect().x + 'px');
    zoomBackdrop.style.background = cfg.zoom.backdrop;
  }
  function showZoomPhoto(idx: number) {
    zoomed = idx;
    syncZoomScrubber(idx);
  }
  function showStack(idx: number) {
    layoutScroller();
    zoomScroll.classList.remove('live');
    scrollToSlot(idx, false);
    zoomScroll.classList.add('live');
    syncZoomScrubber(idx);
  }
  function hideStack() {
    stopZoomScroll();
    setThumbPreview(-1);
    unmagnify(true);
    zoomScroll.classList.remove('live');
  }
  function openZoom(idx: number) {
    if (zoomed >= 0 || idx < 0) return;
    zoomAnim?.stop();
    const transitionId = ++zoomTransitionId;
    closing = false;
    ensureLightboxContent();
    showZoomPhoto(idx);
    setChrome(0);
    zoom.classList.add('on');
    lockPage();
    applyZoomStyle();
    showStack(idx);
    focusZoom(showAllButton instanceof HTMLElement ? showAllButton : null);
    if (reducedMotion.matches) {
      setChrome(1);
      return;
    }
    zoomAnim = animate(0, 1, {
      ...cfg.zoom.open,
      onUpdate: setChrome,
    });
    void zoomAnim.then(() => {
      if (transitionId !== zoomTransitionId) return;
      zoomAnim = null;
      setChrome(1);
    });
  }
  function finishOpening() {
    if (!zoomAnim || closing) return;
    ++zoomTransitionId;
    zoomAnim.stop();
    zoomAnim = null;
    setChrome(1);
  }
  function closeZoom(immediate = false) {
    if (zoomed < 0 || (closing && !immediate)) return;
    const transitionId = ++zoomTransitionId;
    closing = true;
    stopZoomScroll(false);
    setThumbPreview(-1);
    zoomAnim?.stop();
    const initialOpacity = Number(zoomBackdrop.style.opacity);
    const finishClose = () => {
      if (transitionId !== zoomTransitionId) return;
      setChrome(0);
      zoom.classList.remove('on');
      hideStack();
      const gridIndex = Math.min(zoomed, stagePhotos.length - 1);
      const [first, last] = rangeAt(stage.scrollLeft);
      if (gridIndex < first || gridIndex > last) scrollToPhoto(gridIndex, false);
      unlockPage();
      zoomAnim = null;
      closing = false;
      zoomed = -1;
      restoreFocus();
    };
    if (reducedMotion.matches || immediate) {
      finishClose();
      return;
    }
    zoomAnim = animate(initialOpacity, 0, {
      ...cfg.zoom.close,
      onUpdate: setChrome,
    });
    void zoomAnim.then(finishClose);
  }
  function zoomStep(dir: number) {
    if (closing) return;
    finishOpening();
    const n = zoomed + dir;
    if (n < 0 || n >= ALL_PHOTOS.length) return;
    const thumbnailHadFocus = zoomScrubber.contains(document.activeElement);
    unmagnify(true);
    showZoomPhoto(n);
    if (thumbnailHadFocus) zoomThumbButtons[n].focus({ preventScroll: true });
    if (zoomScroll.classList.contains('live')) scrollToSlot(n, true);
  }
  const onSlotClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element)) return;
    if (panMoved || closing) return;
    const frame = frameOf(e.target);
    if (frame) {
      if (isPhone(window.innerWidth)) return;
      if (magnified) unmagnify();
      else magnifyAt(frame, e.clientX, e.clientY);
      return;
    }
    closeZoom();
  };
  zoomScroll.addEventListener('click', onSlotClick);

  const WHEEL_IDLE_MS = 120;
  let wheel: { v: number; lastT: number } | null = null;
  let wheelT: ReturnType<typeof setTimeout> | undefined;
  const onWheel = (e: WheelEvent) => {
    if (zoomed >= 0) return;
    const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey;
    if (!horizontal) return;
    e.preventDefault();
    let d = e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (e.deltaMode === 1) d *= 16;
    else if (e.deltaMode === 2) d *= stage.clientWidth;
    const now = performance.now();
    if (!wheel) {
      if (drag) return;
      stopAnim();
      navTarget = null;
      beginLive();
      wheel = { v: 0, lastT: now };
    }
    const dt = Math.max(1, now - wheel.lastT);
    wheel.v = 0.75 * wheel.v + 0.25 * (d / dt);
    wheel.lastT = now;
    stage.scrollLeft = Math.max(0, Math.min(maxScroll(), stage.scrollLeft + d));
    syncScrubber();
    clearTimeout(wheelT);
    wheelT = setTimeout(() => {
      const w = wheel;
      wheel = null;
      if (w) settleSnap({ v: w.v, gain: 1 });
    }, WHEEL_IDLE_MS);
  };
  stage.addEventListener('wheel', onWheel, { passive: false });
  const cellIndexOf = (target: EventTarget | null) => {
    const cell = target instanceof Element ? target.closest('.cell') : null;
    return cell ? cells.findIndex((c) => c.el === cell) : -1;
  };
  const onCellClick = (e: MouseEvent) => {
    if (suppressCellClick) return;
    const idx = cellIndexOf(e.target);
    if (idx >= 0) openZoom(idx);
  };
  track.addEventListener('click', onCellClick);
  const onShowAll = () => openZoom(0);
  showAllButton?.addEventListener('click', onShowAll);
  // A click listener receives the event. closeZoom treats a truthy argument as
  // "skip the animation", so the event must not be forwarded.
  const closeZoomFromClick = () => {
    closeZoom();
  };
  zoomBackdrop.addEventListener('click', closeZoomFromClick);
  const PINCH_MIN = 2;
  const onPinch = (e: WheelEvent) => {
    if (!e.ctrlKey) return;
    if (zoomed >= 0) {
      if (isPhone(window.innerWidth)) return;
      e.preventDefault();
      if (e.deltaY > PINCH_MIN) closeZoom();
    } else if (e.deltaY < -PINCH_MIN) {
      const idx = cellIndexOf(e.target);
      if (idx < 0) return;
      e.preventDefault();
      openZoom(idx);
    }
  };
  zoom.addEventListener('wheel', onPinch, { passive: false });
  stage.addEventListener('wheel', onPinch, { passive: false });
  const onWinResize = () => {
    if (zoomed < 0) return;
    if (isPhone(window.innerWidth)) unmagnify(true);
    if (closing) {
      closeZoom(true);
      return;
    }
    ++zoomTransitionId;
    stopZoomScroll();
    setThumbPreview(-1);
    zoomAnim?.stop();
    zoomAnim = null;
    applyZoomStyle();
    layoutScroller();
    setChrome(1);
    showStack(zoomed);
  };
  window.addEventListener('resize', onWinResize);

  const onResize = () => {
    if (stagePhotos.length === 0) return;
    const anchor = visibleRange()[0];
    stopAnim();
    stopBlend();
    liveMode = false;
    fromAv = null;
    fromRange = null;
    const phone = isPhone(root.clientWidth);
    if (phone !== stageIsPhone) {
      clearMobileScrubTimer();
      mobileScrub = null;
      setMobileScrubPresentation(false);
      stageIsPhone = phone;
      stagePhotos = stageIsPhone ? ALL_PHOTOS : GRID_PHOTOS;
      buildStage();
      buildThumbs();
    } else {
      sizeColumns();
      sizeThumbs();
    }
    sizeMobileIndicator();
    scrollToPhoto(Math.min(anchor, stagePhotos.length - 1), false);
  };
  function applyFrame() {
    const g = geometry(root.clientWidth, cfg.grid);
    root.style.height = g.frameH + 'px';
    root.style.setProperty('--pad', cfg.grid.padding + 'px');
    root.style.setProperty('--gap', cfg.grid.gap + 'px');
    root.style.setProperty('--stage-radius', cfg.grid.topRadius + 'px');
    root.style.setProperty('--hero-w', g.heroW + 'px');
    root.style.setProperty('--hero-h', g.heroH + 'px');
  }
  applyFrame();
  applyZoomStyle();
  let lastW = root.clientWidth;
  const ro = new ResizeObserver(() => {
    if (root.clientWidth === lastW) return;
    lastW = root.clientWidth;
    applyFrame();
    onResize();
  });

  buildStage();
  buildThumbs();
  sizeMobileIndicator();
  // The grid now shows the hero itself.
  const ssrHero = root.querySelector('.ssr-hero');
  if (ssrHero instanceof HTMLElement) ssrHero.hidden = true;
  rTo = rangeAt(0);
  blend = 1;
  syncScrubber();
  ro.observe(root);

  return {
    closeZoom,
    destroy: () => {
      unlockPage();
      ro.disconnect();
      clearMobileScrubTimer();
      mobileScrub = null;
      setMobileScrubPresentation(false);
      stage.removeEventListener('touchmove', onStageTouchMove);
      stopAnim();
      stopBlend();
      clearTimeout(wheelT);
      ++zoomTransitionId;
      zoomAnim?.stop();
      stopZoomScroll();
      zoomScroll.removeEventListener('scroll', onZoomScroll);
      zoomScroll.removeEventListener('pointerdown', onZoomPointerDown);
      zoomScroll.removeEventListener('wheel', onZoomWheel);
      document.body.style.cursor = '';
      if (!inert) window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onWinResize);
      zoomScrubber.removeEventListener('click', onZoomScrubberClick);
      zoomScrubber.removeEventListener('pointermove', onThumbPointerMove);
      zoomScrubber.removeEventListener('pointerleave', onThumbPointerLeave);
      zoomScrubber.removeEventListener('focusin', onThumbFocus);
      zoomScrubber.removeEventListener('focusout', onThumbBlur);
      zoomScrubber.removeEventListener('scroll', onThumbScroll);
      showAllButton?.removeEventListener('click', onShowAll);
      zoom.classList.remove('on');
    },
  };
}

const VIEWER_SRCSET_PRESETS = [
  ['3:2-sm', 768],
  ['3:2-md', 1200],
  ['3:2-lg', 1440],
  ['3:2-xl', 1800],
] as const;

const viewerPhotoSrcSet = (photo: GalleryPhoto): string | undefined => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (!source) return undefined;
  return VIEWER_SRCSET_PRESETS.map(
    ([preset, width]) =>
      `${buildWorkerPhotoUrl(source, { preset, presetScale: '1', dpr: '1' })} ${width}w`,
  ).join(', ');
};

const viewerPhotoSrc = (photo: GalleryPhoto): string => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (source) return buildWorkerPhotoUrl(source, { preset: '3:2-xl', presetScale: '1', dpr: '1' });
  return pickFirstPresizedUrl(photo, ['xl', 'lg', 'md']) ?? photo.url;
};

const isWideViewerPhoto = (index: number): boolean =>
  index === 0 || (index >= 5 && (index - 5) % 3 === 0);

/** Floor plans belong to their own section rather than the listing photo gallery. */
const buildViewerPhotos = (property: PropertyDetailsType): ViewerPhoto[] =>
  buildGalleryPhotos(property)
    .filter((photo) => !photo.isFloorPlan)
    .map((photo, index) => ({
      src: viewerPhotoSrc(photo),
      srcSet: viewerPhotoSrcSet(photo),
      thumb: getLightboxThumbnailUrl(photo),
      alt: `Photo ${index + 1} of ${property.street ?? 'this home'}`,
      wide: isWideViewerPhoto(index),
    }));

type PdpPhotoViewerProps = {
  property: PropertyDetailsType;
  heroOverlay?: ReactNode;
  showTour3d?: boolean;
};

export const PdpPhotoViewer = ({ property, heroOverlay, showTour3d }: PdpPhotoViewerProps) => {
  const photoDetailsId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<ReturnType<typeof mountViewer> | null>(null);
  // The zoom layer portals into document.body, so it only renders once we are on the client.
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  // Built once per mounted viewer (the parent remounts it per home via `key`).
  const [{ photos, gridPhotos }] = useState(() => {
    const allPhotos = buildViewerPhotos(property);
    return { photos: allPhotos, gridPhotos: allPhotos.slice(0, 5) };
  });

  useEffect(() => {
    const root = rootRef.current;
    const zoom = zoomRef.current;
    if (!mounted || !root || !zoom) return undefined;
    const controls = mountViewer(root, zoom, gridPhotos, photos, CONFIG);
    controlsRef.current = controls;
    return () => {
      controlsRef.current = null;
      controls.destroy();
    };
  }, [gridPhotos, mounted, photos]);
  const address = formatStreetAddress(property.street, property.unitType, property.unitNumber);
  const tour3dUrl = property.tour3d?.url;
  const locality = [[property.city, property.state].filter(Boolean).join(', '), property.zip]
    .filter(Boolean)
    .join(' ');
  const emptyLocation =
    photos.length === 0
      ? streetViewLocationFor({
          availableSoon: property.listingState === 'AVAILABLE_SOON',
          photoCount: 0,
          latitude: property.latitude,
          longitude: property.longitude,
        })
      : null;

  const price = getDisplayPrice(property, 'listed');
  const homeFacts = [
    price != null ? formatCurrency(price) : null,
    property.bedrooms != null ? `${property.bedrooms} bd` : null,
    property.bathrooms != null ? `${property.bathrooms} ba` : null,
    property.sqFtTotalLiving != null
      ? `${property.sqFtTotalLiving.toLocaleString('en-US')} sqft`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const homeDetails = (
    <>
      <p className="zoom-details-address">{[address, locality].filter(Boolean).join(', ')}</p>
      {homeFacts && <p className="zoom-details-facts">{homeFacts}</p>}
    </>
  );
  const zoomLayer = (
    <div
      ref={zoomRef}
      className="pv-zoom"
      aria-label="Home photos"
      aria-describedby={photoDetailsId}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
    >
      <div className="zoom-backdrop" data-pv="zoomBackdrop" />
      <div className="zoom-bar" data-pv="zoomBar">
        <Button
          type="button"
          variant="secondary"
          size="md"
          analyticsName="pdp-lightbox-close"
          className="zoom-close text-od-content-primary"
          aria-label="Close photos"
          onClick={() => controlsRef.current?.closeZoom()}
        >
          <IconX size={16} stroke={2.2} aria-hidden="true" />
        </Button>
      </div>
      <div className="zoom-mobile-details" data-pv="zoomMobileDetails" aria-hidden="true">
        {homeDetails}
      </div>
      <div id={photoDetailsId} data-pv="zoomDetails" hidden>
        {homeDetails}
      </div>
      <div className="zoom-scroll" data-pv="zoomScroll" />
      <div className="zoom-thumb-preview" data-pv="thumbPreview" aria-hidden="true">
        <img alt="" draggable="false" />
        <div className="zoom-thumb-preview-caption">
          <span className="zoom-thumb-preview-count" data-pv="previewCount" />
        </div>
      </div>
      <div
        className="zoom-scrubber"
        data-pv="zoomScrubber"
        role="navigation"
        aria-label="Lightbox photo scrubber"
      />
    </div>
  );

  let stageHero: ReactNode;
  if (photos.length > 0) {
    const hero = photos[0];
    stageHero = (
      <img
        className="ssr-hero"
        src={hero.src}
        srcSet={hero.srcSet}
        sizes={hero.srcSet ? VIEWER_IMAGE_SIZES : undefined}
        alt={hero.alt}
        fetchPriority="high"
        decoding="async"
        draggable="false"
      />
    );
  } else if (emptyLocation) {
    stageHero = (
      <div className="empty-hero">
        <StreetViewPhoto
          latitude={emptyLocation.latitude}
          longitude={emptyLocation.longitude}
          alt={streetViewAltText(property)}
          fallbackLabel="Photos coming soon"
        />
      </div>
    );
  } else {
    stageHero = (
      <div className="empty-hero">
        <PropertyPhotoPlaceholder
          state="unavailable"
          label={property.listingState === 'AVAILABLE_SOON' ? 'Photos coming soon' : undefined}
        />
      </div>
    );
  }

  return (
    <div ref={rootRef} className={photos.length === 0 ? 'pv-root empty' : 'pv-root'}>
      {/* Inserted as raw text so server and browser produce the identical style node. */}
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      {mounted && createPortal(zoomLayer, document.body)}
      <div className="viewer" data-pv="viewer">
        <div className="counter" data-pv="counter">
          1 of {photos.length}
        </div>
        <div className="stage" data-pv="stage">
          {/* Visible before hydration (and without JavaScript); the engine hides it once the grid is built. */}
          {stageHero}
          <div className="track" data-pv="track" />
          {heroOverlay && (
            <div className="hero-overlay" data-pv="heroOverlay">
              {heroOverlay}
            </div>
          )}
          {showTour3d && tour3dUrl && (
            <div className="tour-3d" data-pv="tour3d">
              <GalleryMediaActions tour3dUrl={tour3dUrl} addressLabel={address} />
            </div>
          )}
          {photos.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="md"
              analyticsName="pdp-refresh-show-all-photos"
              aria-label={`Show all ${photos.length} photos`}
              className="gallery-action-button show-all-photos"
              data-pv="showAll"
            >
              <IconLibraryPhoto size={18} stroke={2} aria-hidden="true" />
              <span>Show all {photos.length} photos</span>
            </Button>
          )}
        </div>
        <div className="scrubber" data-pv="scrubber">
          <div className="strip-wrap">
            <div className="pill" data-pv="pill">
              <div className="clip" data-pv="clip">
                <div className="rail" data-pv="rail">
                  <div className="window" data-pv="window" />
                  <div className="thumbs" data-pv="thumbs" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={photos.length === 1 ? 'mobile-page-indicator single' : 'mobile-page-indicator'}
          data-pv="mobileIndicator"
          aria-hidden="true"
        >
          {photos.map((photo, index) => (
            <span
              key={photo.src}
              className={index === 0 ? 'mobile-page-segment active' : 'mobile-page-segment'}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
